<script setup lang="ts">
import { useHelpeeData } from '@/api/complete-helpee-signup/use-helpee-data';
import { hexToRgb } from '@/utils/color';
import {
  FieldSelect,
  BaseButton,
  FieldTextarea,
} from '@magma-app/magma-lapilli';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps<{
  isLoading?: boolean;
}>();

const emit = defineEmits(['submit']);

const route = useRoute();

const criterias = ref<i[]>([]);
const expectation = ref<string>('');

const { data } = useHelpeeData(
  route.params.campaignHelpeeUuid as string | undefined
);

interface i {
  id: number;
  answerKey?: string;
  answerKeys?: string[];
}

const isSubmitDisabled = computed(() => {
  return (
    criterias.value.filter((crit) => crit.answerKey || crit.answerKeys)
      .length !== data.value?.signUpInformation?.criteria.length
    || expectation.value === ''
    || props.isLoading === true
  );
});

const handleSubmit = () => {
  if (isSubmitDisabled.value) {
    return;
  }

  emit('submit', {
    criterias: criterias.value,
    expectation: expectation.value,
  });
};
</script>

<template>
  <div class="px-5 lg:px-10 py-5 lg:py-7 flex flex-col flex-1 justify-between">
    <div>
      <h2 class="text-mgm-txt-xl font-medium">
        {{ $t('importProspects.needs.title') }}
      </h2>
      <h3 class="text-mgm-txt-sm text-foreground-default mb-6">
        {{ $t('importProspects.needs.subtitle') }}
      </h3>
      <FieldSelect
        v-for="(crit, index) in data?.signUpInformation?.criteria"
        :key="index"
        :options="
          crit.options.map((o: any) => ({ label: o.value, value: o.key }))
        "
        :placeholder="crit.label"
        :label="crit.label"
        required
        :multiple="crit.type === 'multiSelect'"
        :custom-color="data?.signUpInformation?.primaryColor"
        :custom-rgb-color="
          hexToRgb(data?.signUpInformation?.primaryColor || '')
        "
        class="mb-6"
        @update:model-value="
          (value) => {
            const newCriteria = {
              id: crit.id,
              [crit.type === 'multiSelect' ? 'answerKeys' : 'answerKey']: value
            };

            criterias = [
              ...criterias.filter((c) => c.id !== crit.id),
              newCriteria
            ];
          }
        "
      />
      <FieldTextarea
        v-model="expectation"
        required
        :label="$t('importProspects.needs.expectationLabel')"
        :placeholder="$t('importProspects.needs.expectationPlaceholder')"
        :custom-color="data?.signUpInformation?.primaryColor"
        :custom-rgb-color="
          hexToRgb(data?.signUpInformation?.primaryColor || '')
        "
      />
    </div>
    <BaseButton
      size="lg"
      class="w-full mb-8 mt-10"
      :custom-color="data?.signUpInformation?.primaryColor"
      :custom-rgb-color="hexToRgb(data?.signUpInformation?.primaryColor || '')"
      :disabled="isSubmitDisabled"
      :is-loading="props.isLoading"
      @click="handleSubmit"
    >
      Continue</BaseButton
    >
  </div>
</template>
